import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/authService';
import { log } from '@/utils/vueLogger.js';
import { CacheManager } from '@/utils/cacheManager.js';

const posTypeCache = new CacheManager('VueCache', 'cache', {
	maxAge: 24 * 60 * 60 * 1000,
	memoryCacheTTL: 30 * 60 * 1000,
	logCategory: 'PosType',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.posTypes.`,
});

export async function getPosTypes(forceRefresh = false) {
	if (!forceRefresh) {
		const memCached = posTypeCache.getFromMemory('all');
		if (memCached) return memCached;

		try {
			const legacyData = await iframeDataBridge.getStorageAsync('POSTYPES');
			if (legacyData) {
				log('info', 'PosType', '📦 Seeding Vue cache from legacy storage (POSTYPES)');
				await posTypeCache.put('all', legacyData);
				posTypeCache.setInMemory('all', legacyData);
				return legacyData;
			}
		} catch (legacyError) {
			log('warn', 'PosType', `⚠️ Legacy storage check failed: ${legacyError.message}`);
		}
	}

	try {
		const cached = await posTypeCache.get('all');
		if (cached && !forceRefresh) {
			posTypeCache.setInMemory('all', cached);

			if (!iframeDataBridge.isOnline()) {
				log('info', 'PosType', '📴 Offline: Returning cached POS types');
				return cached;
			}

			setTimeout(() => refreshPosTypesInBackground(), 100);
			return cached;
		}

		if (!iframeDataBridge.isOnline()) {
			log('warn', 'PosType', '📴 Offline: No cached POS types available');
			return [];
		}

		return await fetchPosTypesFromAPI();
	} catch (error) {
		log('error', 'PosType', `❌ Error getting POS types: ${error.message}`);

		try {
			const fallback = await posTypeCache.get('all');
			if (fallback) {
				log('info', 'PosType', '💾 Using fallback cached POS types');
				return fallback;
			}
		} catch (cacheError) {
			log('error', 'PosType', `❌ Cache fallback failed: ${cacheError.message}`);
		}

		return [];
	}
}

async function fetchPosTypesFromAPI() {
	try {
		const response = await authenticatedFetch(getApiUrl('Sales/POSTypes'), { method: 'GET' }, 'tokenSales');

		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

		const posTypes = await response.json();
		await posTypeCache.put('all', posTypes);
		posTypeCache.setInMemory('all', posTypes);

		log('success', 'PosType', `✅ Fetched and cached ${posTypes.length} POS types`);
		return posTypes;
	} catch (error) {
		log('error', 'PosType', `❌ Failed to fetch POS types from API: ${error.message}`);
		throw error;
	}
}

async function refreshPosTypesInBackground() {
	try {
		await fetchPosTypesFromAPI();
		log('success', 'PosType', '🔄 Background refresh of POS types completed');
	} catch (error) {
		log('warn', 'PosType', `⚠️ Background refresh failed: ${error.message}`);
	}
}

export async function refreshPosTypes() {
	posTypeCache.clearMemory();
	await posTypeCache.clear();
}
